import React, {Component} from "react";
import {Collapse, Container, Nav, Navbar, NavbarBrand, NavItem, NavLink} from "reactstrap";
import {FormattedMessage} from "react-intl";
import _ from "underscore";
import {UserContext, UserContextData} from "../context/UserContext";
import {Platform} from "../data/entities/PlatformEntity";
import {subscriptionTypes} from "../data/entities/UserSubscriptionInfoEntity";
import {getPlatform, scrollToElement} from "../utils/browser";
import {withRouter, RouteComponentProps, Link} from "react-router-dom";
import {LocaleContext} from "../context/LocaleContext";

const logo = require("../resources/img/logo.svg");

interface State {
    isOpen: boolean;
    menuItems: MenuItem[];
    activeItem: number;
    userContext?: UserContextData;
}

interface MenuItem {
    text: JSX.Element,
    display: boolean,
    countryFilter?: string,
    subscriptionTypeFilter?: string[],
    subscriptionPrivilegeProgression?: boolean,
    anchorId?: string,
    href?: string,
    targetSelf?: boolean,
    className?: string
}

class TopNav extends Component<RouteComponentProps, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    private platform: Platform = getPlatform();

    state: State = {
        isOpen: false,
        menuItems: [],
        activeItem: -1,
    };

    loggedInMenu: MenuItem[] = [
        {
            text: <FormattedMessage id="navbar.links.status"/>,
            anchorId: "nav-anchor-progression",
            countryFilter: process.env.REACT_APP_COUNTRIES_PROGRESSION,
            subscriptionTypeFilter: [subscriptionTypes.premium, subscriptionTypes.premiumAnywhere],
            subscriptionPrivilegeProgression: true,
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.featured"/>,
            anchorId: "nav-anchor-featured",
            className: "featured-link",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.rewards"/>,
            anchorId: "nav-anchor-rewards",
            countryFilter: process.env.REACT_APP_COUNTRIES_REWARDS,
            subscriptionTypeFilter: [subscriptionTypes.premium, subscriptionTypes.premiumAnywhere],
            subscriptionPrivilegeProgression: true,
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.library"/>,
            anchorId: "nav-anchor-all-games",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.benefits"/>,
            anchorId: "nav-anchor-benefits",
            countryFilter: process.env.REACT_APP_COUNTRIES_PROGRESSION,
            subscriptionTypeFilter: [subscriptionTypes.premium, subscriptionTypes.premiumAnywhere],
            subscriptionPrivilegeProgression: true,
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.subscription"/>,
            href: ((): string => {
                if (this.platform === "upc") {
                    return `${process.env.REACT_APP_UPC_CLIENT_STORE_MANAGE_SUBSCRIPTION_URL}`
                }
                return `${process.env.REACT_APP_WEB_CLIENT_STORE_MANAGE_SUBSCRIPTION_URL}`
            })(),
            targetSelf: this.platform !== "web",
            display: this.platform !== "upc_steam"
        }
    ];

    loggedOutMenu: MenuItem[] = [
        {
            text: <FormattedMessage id="navbar.links.discover"/>,
            href: "https://store.ubi.com/uplayplus",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.store"/>,
            href: "https://store.ubi.com/",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.news"/>,
            href: "https://www.ubisoft.com/",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.forum"/>,
            href: "https://forums.ubisoft.com/forumdisplay.php/2192-Uplay",
            display: true
        },
        {
            text: <FormattedMessage id="navbar.links.support"/>,
            href: "https://support.ubisoft.com/platforms/59",
            display: true
        }
    ];

    componentDidUpdate(): void {
        if (this.context !== this.state.userContext) {
            this.generateMenuItems();
        }
    }

    componentDidMount(): void {
        this.generateMenuItems();
        window.addEventListener('scroll', this.setActiveMenuItem);
    }

    componentWillUnmount(): void {
        window.removeEventListener('scroll', this.setActiveMenuItem);
    }

    render(): JSX.Element {
        return (
            <Navbar dark expand="xl">
                <LocaleContext.Consumer>
                    {({locale}) => (
                        <Container>
                            <button className="navbar-toggler" onClick={this.toggle}>
                                <span id="nav-icon3"><span/><span/><span/><span/></span>
                            </button>

                            <NavbarBrand
                                tag={Link}
                                to={`/${locale}`}
                                onClick={() => {
                                    this.setState({activeItem: 0});
                                    this.close();
                                    window.scrollTo({top: 0});
                                }}
                            >
                                <img src={logo} alt="Ubisoft+"/>
                            </NavbarBrand>

                            <Collapse isOpen={this.state.isOpen} navbar>
                                <Nav navbar>
                                    {
                                        this.state.menuItems.map((item, index) => {
                                            if (item.display) {
                                                let onClick = undefined;
                                                if (item.anchorId) {
                                                    let anchor = item.anchorId;
                                                    onClick = () => {
                                                        this.setState({activeItem: index});
                                                        setTimeout(() => {
                                                            this.navigateAnchor(anchor);
                                                        }, 100)
                                                    };
                                                }

                                                return (
                                                    <NavItem
                                                        key={index}
                                                        active={index === this.state.activeItem}
                                                        className={item.className}
                                                    >
                                                        <NavLink
                                                            href={item.href}
                                                            onClick={onClick}
                                                            target={(item.href && !item.targetSelf) ? "_blank" : "_self"}
                                                            tag={item.href ? undefined : Link}
                                                            to={item.href ? undefined : {
                                                                pathname: `/${locale}`,
                                                                search: window.location.search,
                                                            }}
                                                        >
                                                            {item.text}
                                                        </NavLink>
                                                    </NavItem>
                                                );
                                            }

                                            return null;
                                        })
                                    }

                                    {
                                        this.context.validateAnywhereAccess() &&
                                            <NavItem className="cloud-anywhere"
                                                key={this.state.menuItems.length}
                                                active={this.props.location.pathname === `/${locale}/cloudgaming`}
                                            >
                                                <NavLink
                                                    onClick={() => {
                                                        this.setState({activeItem: -1});
                                                        this.close();
                                                        window.scrollTo({top: 0});
                                                    }}
                                                    tag={Link}
                                                    to={{
                                                        pathname: `/${locale}/cloudgaming`,
                                                        search: window.location.search,
                                                    }}
                                                >
                                                    <FormattedMessage id="navbar.links.cloud"/>
                                                </NavLink>
                                            </NavItem>
                                    }
                                </Nav>
                            </Collapse>

                            <div id="overlay-widget"/>
                        </Container>
                    )}
                </LocaleContext.Consumer>
            </Navbar>
        );
    }

    navigateAnchor = (anchor: string) => {
        this.close();
        scrollToElement(anchor);
    };

    generateMenuItems = () => {
        let menuItems: MenuItem[] = [];

        if (this.context.authVerified) {
            if (this.context.subscriptionVerified) {
                if (this.context.user.isSubscribed) {
                    menuItems = this.loggedInMenu;
                } else {
                    menuItems = this.loggedOutMenu;
                }
            } else if (!this.context.user.isAuthenticated) {
                menuItems = this.loggedOutMenu;
            }
        }

        this.setState(
            {
                menuItems: menuItems.filter(item => (
                        (!item.countryFilter || this.context.validateUserCountry(item.countryFilter)) &&
                        (!item.subscriptionTypeFilter || this.context.validateProgressionType(item.subscriptionTypeFilter)) &&
                        (!item.subscriptionPrivilegeProgression || this.context.validateSubscriptionProgressionPrivilege())
                    )
                ),
                userContext: this.context,
            },
            this.setActiveMenuItem
        );
    };

    setActiveMenuItem = _.debounce(() => {
        const activeItem = this.getActiveMenuItem();

        if (activeItem !== this.state.activeItem) {
            this.setState({activeItem: activeItem})
        }
    }, 300);

    getActiveMenuItem = (): number => {
        let activeItem: number = -1;
        let activeOffset: number = 150;

        this.state.menuItems.forEach((item, index) => {
            if (item.anchorId) {
                let element = document.getElementById(item.anchorId);
                if (element) {
                    let elementPosition = element.getBoundingClientRect().top - activeOffset;

                    if (elementPosition <= 0) {
                        activeItem = index;
                    }
                }
            }
        });

        return activeItem;
    };

    toggle = () => {
        let open = !this.state.isOpen;

        this.setState({isOpen: open}, () => {
            if (open) {
                document.body.classList.add("nav-open");
            } else {
                document.body.classList.remove("nav-open");
            }
        });
    };

    close = () => {
        if (this.state.isOpen) {
            this.toggle();
        }
    };
}

export default withRouter(TopNav);